import React from 'react';

import { Grid, GridItem } from '@chakra-ui/react';

import { OfferingSmallCard } from '@r-client/republic/feature/offering-list';
import { WysiwygStyles } from '@r-client/republic/ui/core';
import {
  Box,
  defaultPlaceholder,
  Divider,
  Img,
  Spacer,
  Text,
} from '@r-client/shared/ui/core';
import { SafeHTML } from '@r-client/shared/util/components';

import { TGetEventsEventDetails } from '../../types';
import { isCrowdcast } from '../../utils';
import { SEOStructuredData } from '../seo-structured-data';
import { CrowdcastWidget } from './crowdcast-widget/crowdcast-widget';
import { Header } from './header/header';
import { HostList } from './host-list/host-list';
import { TagList } from './tag-list/tag-list';

import styles from './event-details.module.scss';
export interface IEventDetailsProps {
  event: NonNullable<TGetEventsEventDetails>;
  onModalOpen: (id: string) => void;
  onWindowOpen: (url: string) => void;
}

export enum E_TEST_IDS {
  eventDetails = 'eventDetails',
  calendarDate = 'calendarDate',
  eventHours = 'eventHours',
  hostList = 'hostList',
  tagList = 'tagList',
  seoData = 'seoData',
  crowdcastWidget = 'crowdcastWidget',
  addToCalendarMenu = 'addToCalendarMenu',
}

export const EventDetails: React.FC<
  React.PropsWithChildren<IEventDetailsProps>
> = ({ event, onModalOpen, onWindowOpen }) => {
  const hasOfferings = !!(event?.offerings && event?.offerings.length > 0);
  const offeringList = event?.offerings;

  return (
    <article data-testid={E_TEST_IDS.eventDetails}>
      <Box m={Box.bottom(2)}>
        <Img
          aspectRatio="16/9"
          fullWidth
          fit="cover"
          alt={event.title}
          src={event.coverPhotoUrl ?? defaultPlaceholder}
          className={styles.coverPhoto}
        />
      </Box>

      <Header
        event={event}
        onModalOpen={onModalOpen}
        onWindowOpen={onWindowOpen}
        dataTestIds={E_TEST_IDS}
      />

      <Grid templateColumns="repeat(24, 1fr)" gap={4}>
        <GridItem colSpan={{ base: 22, sm: 21 }} colStart={{ sm: 4 }}>
          <Divider margin="2" />

          {event.body && (
            <WysiwygStyles>
              <SafeHTML
                sanitizedHtml={event.body}
                className="body"
                component={Box}
              />
            </WysiwygStyles>
          )}

          {event.url && isCrowdcast(event.url) && (
            <>
              <Spacer size="1.5brm" />
              <CrowdcastWidget
                title={event.title}
                url={event.url}
                dataTestId={E_TEST_IDS.crowdcastWidget}
              />
            </>
          )}
        </GridItem>
      </Grid>

      {!!event.xHosts?.length && (
        <>
          <Divider margin={{ top: '4', bottom: '2' }} />
          <section data-testid={E_TEST_IDS.hostList}>
            <Text
              as="h3"
              color="gray70"
              weight="600"
              className={styles.sectionHeading}
            >
              Participants
            </Text>
            <Spacer size="1.5brm" />
            <HostList hosts={event.xHosts} />
          </section>
        </>
      )}

      {hasOfferings && (
        <>
          <Divider margin={{ top: '4', bottom: '2' }} />
          <Text
            as="h3"
            color="gray70"
            weight="600"
            className={styles.sectionHeading}
          >
            Featuring
          </Text>
          <Spacer size="1.5brm" />
          <Box className={styles.eventsOfferingGrid}>
            {offeringList.map((offering, index) => {
              return (
                <Box key={index} className={styles.eventsOfferingGridItem}>
                  <OfferingSmallCard
                    slug={offering.slug}
                    companyName={offering.companyName || ''}
                    description={offering.description || ''}
                    cover={offering.cardImageUrl || ''}
                    logoUrl={offering.logoUrl || ''}
                    indexInAllOfferings={offering.indexInAllOfferings || 0}
                    numOfAllOfferings={offering.indexInAllOfferings || 0}
                    tooltipText={offering.cardTooltipText || ''}
                    tags={offering.tags}
                    badges={offering.badges}
                    following={offering.following}
                  />
                </Box>
              );
            })}
          </Box>
        </>
      )}

      {!!event.tags?.length && (
        <Box
          as="section"
          m={{
            xs: '3brm 0 0 0',
            sm: '4brm 0 0 0',
            md: '4brm 0 0 0',
            lg: '4brm 0 0 0',
          }}
        >
          <Grid templateColumns="repeat(24, 1fr)" gap={4}>
            <GridItem colSpan={{ base: 4 }}>
              <Divider margin="1.5" />
            </GridItem>

            <GridItem colSpan={{ base: 24 }}>
              <div className={styles.tagsWrapper}>
                <Text
                  as="h3"
                  type="bodyMedium"
                  color="gray70"
                  weight="300"
                  className={styles.tagsHeader}
                >
                  Tags:
                </Text>
                <TagList tags={event.tags} dataTestId={E_TEST_IDS.tagList} />
              </div>
            </GridItem>
          </Grid>
        </Box>
      )}

      <SEOStructuredData data={event} dataTestId={E_TEST_IDS.seoData} />
    </article>
  );
};
